import React from "react";
import PropTypes from "prop-types";
import { withTheme } from "styled-components";
import { cloud, red, silver } from "@vitalsource/vstui/colors";
import { getLanguages } from "../../../redux/locale/selectors";

const BORDER_WIDTH = 15;
const RADIUS = 90 - BORDER_WIDTH / 2;
const DASH_ARRAY = RADIUS * Math.PI * 2;

const isRtlLang = () => {
  const lang = getLanguages().find(
    ({ short }) => short === i18n.getCurrentLocale()
  );
  return lang && lang.direction === "rtl";
};

const calculateProgress = (current, total) => {
  const progress = current <= total ? (100 * current) / total : 100;
  return isRtlLang() ? progress * -1 : progress;
};

const calculateOffset = progress =>
  DASH_ARRAY - (DASH_ARRAY * progress * 0.5) / 100;

const HalfCircle = ({ color, offset, transform }) => (
  <circle
    cx={90}
    cy={90}
    r={RADIUS}
    strokeWidth={`${BORDER_WIDTH}px`}
    stroke={color}
    transform={transform}
    style={{
      fill: "none",
      strokeDasharray: DASH_ARRAY,
      strokeDashoffset: offset ? offset : DASH_ARRAY / 2
    }}
  />
);

const StripePattern = ({ id, color, angle }) => (
  <pattern
    id={id}
    width="10"
    height="10"
    patternTransform={`rotate(${angle} 0 0)`}
    patternUnits="userSpaceOnUse"
  >
    <line
      x1="0"
      y1="0"
      x2="0"
      y2="10"
      strokeWidth="10px"
      style={{ stroke: color }}
    />
  </pattern>
);

const Printometer = ({ pending, current, total, height, width, theme }) => {
  const isRtl = isRtlLang();
  const progress = calculateProgress(current, total);
  const projected = calculateProgress(
    current + pending <= total ? current + pending : total,
    total
  );
  const overflow = calculateProgress(
    current + pending > total ? current + pending : 0,
    total
  );

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <StripePattern
          id="blue-stripes-neg-45"
          color={theme.vstui.color.primary.getShade(6)}
          angle={-45}
        />
        <StripePattern
          id="blue-stripes-45"
          color={theme.vstui.color.primary.getShade(6)}
          angle={45}
        />
        <StripePattern
          id="blue-stripes-135"
          color={theme.vstui.color.primary.getShade(6)}
          angle={135}
        />
        <StripePattern
          id="red-stripes-neg-45"
          color={red.getShade(6)}
          angle={-45}
        />
        <StripePattern id="red-stripes-45" color={red.getShade(6)} angle={45} />
        <StripePattern
          id="red-stripes-135"
          color={red.getShade(6)}
          angle={135}
        />
      </defs>

      {/* Background */}
      <HalfCircle color={cloud.getShade(3)} transform="rotate(180, 90 90)" />

      {/* Projected valid progress */}
      {pending && [
        <HalfCircle
          key="half-1"
          transform={isRtl ? "rotate(0, 90 90)" : "rotate(180, 90 90)"}
          color={theme.vstui.color.primary.getShade(3)}
          offset={calculateOffset(projected)}
        />,
        <HalfCircle
          key="half-2"
          color={isRtl ? "url(#blue-stripes-45)" : "url(#blue-stripes-neg-45)"}
          transform={isRtl ? "rotate(0, 90 90)" : "rotate(180, 90 90)"}
          offset={
            Math.abs(projected) <= 50
              ? calculateOffset(projected)
              : calculateOffset(isRtl ? -50 : 50)
          }
        />,
        Math.abs(projected) > 50 && (
          <HalfCircle
            key="half-3"
            color={isRtl ? "url(#blue-stripes-45)" : "url(#blue-stripes-135)"}
            transform={"rotate(270, 90 90)"}
            offset={
              isRtl
                ? calculateOffset(projected + 50)
                : calculateOffset(projected - 50)
            }
          />
        )
      ]}

      {/* Projected invalid progress */}
      {current + pending > total && [
        <HalfCircle
          key="half-4"
          transform={isRtl ? "rotate(0, 90 90)" : "rotate(180, 90 90)"}
          color={red.getShade(4)}
          offset={calculateOffset(overflow)}
        />,
        <HalfCircle
          key="half-5"
          color={isRtl ? "url(#red-stripes-45)" : "url(#red-stripes-neg-45)"}
          transform={isRtl ? "rotate(0, 90 90)" : "rotate(180, 90 90)"}
          offset={
            Math.abs(overflow) <= 50
              ? calculateOffset(overflow)
              : calculateOffset(isRtl ? -50 : 50)
          }
        />,
        Math.abs(overflow) > 50 && (
          <HalfCircle
            key="half-6"
            color={isRtl ? "url(#red-stripes-45)" : "url(#red-stripes-135)"}
            transform="rotate(270, 90 90)"
            offset={
              isRtl
                ? calculateOffset(overflow + 50)
                : calculateOffset(overflow - 50)
            }
          />
        )
      ]}

      {/* Current progress */}
      <HalfCircle
        color={theme.vstui.color.primary.getShade(9)}
        offset={calculateOffset(progress)}
        transform={isRtl ? "rotate(0, 90 90)" : "rotate(180, 90 90)"}
      />

      {isRtl ? (
        <text
          x={90}
          y={55}
          dy=".4em"
          textAnchor="middle"
          fontSize="2.5rem"
          style={{ fill: cloud.getShade(8) }}
        >
          <tspan>
            {total}
            {" / "}
          </tspan>
          <tspan
            style={{
              fill:
                current + pending > total
                  ? red.getShade(6)
                  : pending > 0
                  ? theme.vstui.color.primary.getShade(6)
                  : cloud.getShade(8),
              fontWeight:
                current + pending > total || pending ? "bold" : "normal"
            }}
          >
            {current + pending}
          </tspan>
        </text>
      ) : (
        <text
          x={90}
          y={55}
          dy=".4em"
          textAnchor="middle"
          fontSize="2.5rem"
          style={{ fill: cloud.getShade(8) }}
        >
          <tspan
            style={{
              fill:
                current + pending > total
                  ? red.getShade(6)
                  : pending > 0
                  ? theme.vstui.color.primary.getShade(6)
                  : cloud.getShade(8),
              fontWeight:
                current + pending > total || pending ? "bold" : "normal"
            }}
          >
            {current + pending}
          </tspan>
          <tspan>
            {" / "}
            {total}
          </tspan>
        </text>
      )}

      <text
        x={90}
        y={90}
        dy=".4em"
        textAnchor="middle"
        fontSize="1.2rem"
        letterSpacing="0.8"
        style={{ fill: silver.getShade(7), textTransform: "uppercase" }}
      >
        <tspan x={90} y={65} dy="2em">
          {current >= total
            ? i18n.t("print.limit-reached")
            : i18n.t("print.pages-printed")}
        </tspan>
      </text>
    </svg>
  );
};

Printometer.propTypes = {
  total: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  pending: PropTypes.number,
  height: PropTypes.number,
  width: PropTypes.number
};

Printometer.defaultProps = {
  total: 100,
  current: 20,
  pending: 2,
  height: 90,
  width: 180
};

export default withTheme(Printometer);
